import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Chip, CircularProgress, Grid, Snackbar, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { timeZoneConverted } from "@/config";
import { Title } from "@/layouts/PublicPage/styles";
import { FlexExpandingWrapper } from "@/pages/ActivityLibrary/components/ActivityLibraryTable/styles";
import {
  ButtonContainer,
  Discarded,
  GridContainer,
  InfoContainer,
  InfoDiv,
  InfoTitle,
  Loader,
  Note,
  RowTitle,
  UserDetailsBox,
} from "@/pages/UserDetails/styles";
import { HorizontalFlexContainer } from "@/styles/flex";
import { convertActivationStatus } from "@/transformers/account.transformer";
import { transformDate } from "@/utils/date-utils";

import { AddTreatmentModal } from "./components/AddTreatmentModal";
import { UPDATE_TREATMENT } from "./components/EditTreatmentModal/queries";
import {
  UpdateTreatmentResponse,
  UpdateTreatmentVariables,
} from "./components/EditTreatmentModal/types";
import { EditUserModal } from "./components/EditUserModal";
import ImageUploadModal from "./components/ImageUploadModal";
import ProfilePhoto from "./components/ProfilePhoto";
import { TreatmentHistoryTable } from "./components/TreatmentHistoryTable";
import { GET_LATEST_PROGRAM } from "./components/TreatmentHistoryTable/queries";
import { DEACTIVATE_USER, REACTIVATE_USER } from "./queries";
import { MutationInput, UserInfoProps } from "./types";

export const UserInfoComponent = (props: UserInfoProps) => {
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [addTreatmentModalOpen, setaddTreatmentModalOpen] = useState<boolean>(false);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState<boolean>(false);
  const [confirmReactivateOpen, setConfirmReactivateOpen] = useState<boolean>(false);
  const [isUpdatingTreatment, setIsUpdatingTreatment] = useState<boolean>(false);
  const [openImageUploadModal, setOpenImageUploadModal] = useState<boolean>(false);
  const client = useApolloClient();
  const navigate = useNavigate();

  const handleEditUserModalOpen = () => setIsModalOpen(true);
  const handleEditUserModalClose = () => setIsModalOpen(false);
  const handleAddTreatmentModalOpen = () => setaddTreatmentModalOpen(true);
  const handleAddTreatmentModalClose = () => setaddTreatmentModalOpen(false);

  const account = props?.account;

  const AllProgramsData = props?.AllProgramsData;

  const treatmentHistory = AllProgramsData?.treatmentHistory;
  const latestProgramFromHistory =
    treatmentHistory && treatmentHistory.length > 0
      ? treatmentHistory[treatmentHistory.length - 1]
      : null;

  const {
    data: latestProgramData,
    loading: latestProgramLoading,
    refetch: refetchLatestProgram,
  } = useQuery(GET_LATEST_PROGRAM, {
    variables: { accountId: account.id },
    skip: !treatmentHistory || treatmentHistory.length === 0,
  });

  const latestProgram = latestProgramData?.getLatestProgram;

  const [updateProgramHistory, { loading: updateLoading }] = useMutation<
    UpdateTreatmentResponse,
    UpdateTreatmentVariables
  >(UPDATE_TREATMENT, {
    onError: (error) => {
      setErrorMessage(`Failed to update treatment: ${error.message}`);
      setShowError(true);
      setIsUpdatingTreatment(false);
    },
    onCompleted: async () => {
      setIsUpdatingTreatment(false);
      props.ProgramsReftech();
      await refetchLatestProgram();
    },
  });

  useEffect(() => {
    latestProgram;
  }, [latestProgram]);

  const getLatestPlaylistEndDate = () => {
    if (account?.playlists && account.playlists.length > 0) {
      // Sort playlists by end date to get the most recent one
      const sortedPlaylists = [...account.playlists].sort((a, b) => {
        const dateA = a.endOn ? new Date(a.endOn).getTime() : 0;
        const dateB = b.endOn ? new Date(b.endOn).getTime() : 0;
        return dateB - dateA; // Sort in descending order
      });

      const latestPlaylist = sortedPlaylists[0];

      return latestPlaylist.endOn ? new Date(latestPlaylist.endOn) : null;
    }
    return null;
  };

  const handleNewTreatment = async () => {
    try {
      if (latestProgram?.end_date) {
        handleAddTreatmentModalOpen();
        return;
      }

      if (latestProgramFromHistory === null) {
        handleAddTreatmentModalOpen();
        return;
      }

      if (latestProgramFromHistory && latestProgramFromHistory.end_date === null) {
        const playlistEndDate = getLatestPlaylistEndDate();

        if (!playlistEndDate) {
          setErrorMessage(
            "Please assign an end date to the latest intensive record before creating a new treatment record."
          );
          setShowError(true);
          return;
        }

        if (!latestProgramFromHistory.id) {
          setErrorMessage("Latest treatment record ID is missing.");
          setShowError(true);
          return;
        }

        // Keep the original start date as a Date object
        const originalStartDate = new Date(latestProgramFromHistory.start_date);

        // Check if playlist end date is before treatment start date
        if (playlistEndDate <= originalStartDate) {
          // Format dates consistently in UTC
          const formatDate = (date: Date) => {
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, "0");
            const day = String(date.getUTCDate()).padStart(2, "0");
            return `${month}/${day}/${year}`;
          };

          setErrorMessage(
            `The latest playlist ended on ${formatDate(
              playlistEndDate
            )}, which is before the current treatment started on ${formatDate(
              originalStartDate
            )}. Please manually edit the treatment record to add an end date, then create a new treatment.`
          );
          setShowError(true);
          return;
        }

        setIsUpdatingTreatment(true);

        try {
          await updateProgramHistory({
            variables: {
              id: latestProgramFromHistory.id,
              data: {
                start_date: originalStartDate,
                end_date: playlistEndDate,
                specialist_id: latestProgramFromHistory.specialist.id,
              },
            },
          });

          // Refetch the latest program data to ensure we have the updated end date
          await refetchLatestProgram();

          setSuccessMessage("Treatment end date successfully updated from playlist!");
          setShowSuccessMessage(true);
          setIsUpdatingTreatment(false);
          // Only open modal if update was successful
          handleAddTreatmentModalOpen();
        } catch (error: unknown) {
          const updateError = error as Error;
          // eslint-disable-next-line no-console
          console.error("Error updating treatment:", updateError);

          // Check if it's a validation error about dates
          if (updateError.message && updateError.message.includes("start date")) {
            setErrorMessage(
              "Could not auto-update treatment end date. The playlist end date is before the treatment start date. Please manually edit the existing treatment record."
            );
          } else if (
            updateError.message &&
            updateError.message.includes("End date must be after")
          ) {
            setErrorMessage(
              "Could not auto-update treatment end date. The playlist end date must be after the treatment start date. Please manually edit the existing treatment record."
            );
          } else {
            setErrorMessage(
              "Failed to auto-update treatment end date. Please manually edit the treatment record."
            );
          }
          setShowError(true);
          setIsUpdatingTreatment(false);
          // Don't open modal on error - explicit return
          return;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error in handleNewTreatment:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");
      setShowError(true);
      setIsUpdatingTreatment(false);
    }
  };

  const handleImageUploadModalSubmit = async () => {
    await client.refetchQueries({ include: ["GetSingleUser"] });
  };

  const [reactivateUser, { error: reactivateError }] = useMutation<MutationInput>(REACTIVATE_USER, {
    variables: { id: props.account?.id },
  });

  const [deactivateUser, { error: deactivateError }] = useMutation<MutationInput>(DEACTIVATE_USER, {
    variables: { id: props.account?.id },
  });

  useEffect(() => {
    if (reactivateError || deactivateError) {
      setShowError(true);
      setErrorMessage(
        reactivateError
          ? "Failed to reactivate patient."
          : deactivateError
          ? "Failed to deactivate patient."
          : ""
      );
    }
  }, [reactivateError, deactivateError]);

  if (latestProgramLoading) {
    return (
      <FlexExpandingWrapper>
        <Loader>
          <CircularProgress />
        </Loader>
      </FlexExpandingWrapper>
    );
  }

  return (
    <div>
      <UserDetailsBox>
        <Grid container justifyContent="space-between" sx={{ paddingBottom: "0.9rem" }}>
          <Title>Patient Info</Title>
          <ButtonContainer>
            {!account?.deletedAt ? (
              <>
                <Button
                  className="button"
                  variant="outlined"
                  onClick={() => setOpenImageUploadModal(true)}
                >
                  Upload Profile Photo
                </Button>
                <Button
                  className="button"
                  variant="outlined"
                  onClick={() => handleEditUserModalOpen()}
                >
                  Edit
                </Button>
                <Button
                  className="button"
                  color="error"
                  onClick={() => setConfirmDeactivateOpen(true)}
                >
                  Deactivate
                </Button>
              </>
            ) : (
              <Button className="button" onClick={() => setConfirmReactivateOpen(true)}>
                Activate
              </Button>
            )}
          </ButtonContainer>
        </Grid>
        <InfoContainer>
          <GridContainer>
            <RowTitle>Basic Information</RowTitle>
            <ProfilePhoto
              src={account.profilePhotoUrl}
              onPlaceholderClick={() => setOpenImageUploadModal(true)}
            />
            <InfoDiv>
              <InfoTitle>Patient Name</InfoTitle>
              <Typography>{account?.firstName + " " + account?.lastName}</Typography>
            </InfoDiv>

            <InfoDiv>
              <InfoTitle>Moneta Therapist</InfoTitle>
              <Typography>
                {account?.specialist?.firstName === "Heather" ? (
                  <Discarded>{account?.specialist?.firstName}*</Discarded>
                ) : (
                  account?.specialist?.firstName
                )}
              </Typography>
            </InfoDiv>
            <InfoDiv>
              <InfoTitle>EHR Id</InfoTitle>
              <Typography>{account?.Id_Ehr ? account?.Id_Ehr : "-"}</Typography>
            </InfoDiv>

            <InfoDiv>
              <InfoTitle>Time Zone</InfoTitle>
              <Typography>{timeZoneConverted(account?.timeZone ?? "America/New_York")}</Typography>
            </InfoDiv>
          </GridContainer>
          <GridContainer>
            <RowTitle>Contact Information</RowTitle>

            <InfoDiv>
              <InfoTitle>Email</InfoTitle>
              <Typography>{account?.email ?? "-"}</Typography>
            </InfoDiv>
            <InfoDiv>
              <InfoTitle>Activity Phone #</InfoTitle>
              <Typography>{account?.activityPhoneNumber ?? "-"}</Typography>
            </InfoDiv>
            <InfoDiv>
              <InfoTitle>Contact Phone #</InfoTitle>
              <Typography>{account?.contactPhoneNumber ?? "-"}</Typography>
            </InfoDiv>
          </GridContainer>
          <GridContainer>
            <RowTitle>System Information</RowTitle>

            <InfoDiv>
              <InfoTitle>Date Created</InfoTitle>
              <Typography>{transformDate(account?.createdOn)}</Typography>
            </InfoDiv>

            <InfoDiv>
              <InfoTitle>Patient Status</InfoTitle>
              <Typography>
                <Chip
                  label={convertActivationStatus(account?.deletedAt)}
                  color={
                    convertActivationStatus(account?.deletedAt) === "Active" ? "success" : "default"
                  }
                />
              </Typography>
            </InfoDiv>

            <InfoDiv>
              <InfoTitle>Is Test Patient</InfoTitle>
              <Typography>{!account?.isTestUser ? <div>No</div> : <div>Yes</div>}</Typography>
            </InfoDiv>
            <InfoDiv>
              <InfoTitle>Lead Type</InfoTitle>
              <Typography>
                {account?.leadType ? (account?.leadType === "None" ? "" : account?.leadType) : ""}
              </Typography>
            </InfoDiv>
            <InfoDiv>
              <InfoTitle>Call Delivery Speed</InfoTitle>
              <Typography>{account?.callDeliverySpeed}</Typography>
            </InfoDiv>
          </GridContainer>
        </InfoContainer>
        {account?.specialist?.firstName == "Heather" ? (
          <Note>Note: Please change the Moneta therapist from Heather to other therapist</Note>
        ) : (
          ""
        )}
        {(treatmentHistory && treatmentHistory.length > 0) || !account?.deletedAt ? (
          <>
            <HorizontalFlexContainer
              style={{
                margin: "40px 0",
              }}
            >
              <RowTitle>Moneta Treatment History</RowTitle>
              <Button
                className="button"
                onClick={handleNewTreatment}
                disabled={isUpdatingTreatment || updateLoading || !!account?.deletedAt}
              >
                {isUpdatingTreatment || updateLoading ? "Updating..." : "Add Treatment Record"}
              </Button>
            </HorizontalFlexContainer>
            <div style={{ marginTop: "32px" }}>
              <TreatmentHistoryTable userId={account.id} />
            </div>
          </>
        ) : null}
        {confirmDeactivateOpen && (
          <ConfirmDialog
            confirmOpen={confirmDeactivateOpen}
            onSubmit={async () => (
              await deactivateUser(), setConfirmDeactivateOpen(false), navigate(-1)
            )}
            onClose={() => setConfirmDeactivateOpen(false)}
            dialogContent={
              <>
                <Typography>Are you sure you want to deactivate this patient?</Typography>
              </>
            }
          />
        )}
        {confirmReactivateOpen && (
          <ConfirmDialog
            confirmOpen={confirmReactivateOpen}
            onSubmit={async () => (
              await reactivateUser(), setConfirmReactivateOpen(false), navigate(-1)
            )}
            onClose={() => setConfirmReactivateOpen(false)}
            dialogContent={
              <>
                <Typography>Are you sure you want to reactivate this patient?</Typography>
              </>
            }
          />
        )}
      </UserDetailsBox>
      {isModalOpen && (
        <EditUserModal open={isModalOpen} onClose={handleEditUserModalClose} account={account} />
      )}
      {addTreatmentModalOpen && (
        <AddTreatmentModal
          open={addTreatmentModalOpen}
          onClose={handleAddTreatmentModalClose}
          account={account}
          refetch={props.ProgramsReftech}
        />
      )}
      {showError && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showError}
          onClose={() => setShowError(false)}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            {errorMessage || "An error occurred"}
          </Alert>
        </Snackbar>
      )}
      {showSuccessMessage && (
        <Snackbar
          open={showSuccessMessage}
          autoHideDuration={3000}
          onClose={() => setShowSuccessMessage(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={() => setShowSuccessMessage(false)} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>
      )}
      {openImageUploadModal && (
        <ImageUploadModal
          accountId={account.id}
          openModal={openImageUploadModal}
          handleClose={() => setOpenImageUploadModal(false)}
          handleSubmit={handleImageUploadModalSubmit}
        />
      )}
    </div>
  );
};
